import {CheckOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {Button, Modal, Space, Spin, Typography} from 'antd';
import {Auth} from 'aws-amplify';
import {Confirm, ConfirmFormValues} from './Confirm';
import {Init, InitFormValues} from './Init';
import {ModalContent, ModalFooter} from './Shared';
import {useUserContext} from 'hooks/useUserContext';
import React, {useEffect, useState} from 'react';

type Props = {
  headless?: boolean;
  phoneNumber?: string;
};

enum Step {
  init,
  confirm,
  success
}

const {Paragraph} = Typography;

export const EnableSmsMFA: React.FC<Props> = (props) => {
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [mfaType, setMfaType] = useState<string | null>(null);
  const [step, setStep] = useState<Step>(Step.init);
  const {setCurrentUser, userPhone = props.phoneNumber} = useUserContext();
  const [isOpen, setOpen] = useState(false);
  const {headless = false} = props;

  const updateCurrentUser = async (bypassCache = false) => {
    const user = await Auth.currentAuthenticatedUser({bypassCache});
    setCurrentUser(user);
    const mfa = await Auth.getPreferredMFA(user, {bypassCache});
    setMfaType(mfa);
  };

  useEffect(() => {
    updateCurrentUser(headless);
  }, [step, headless]);

  useEffect(() => {
    if (headless && mfaType === 'NOMFA') {
      setOpen(true);
    }
  }, [mfaType]);

  const handleInit = async ({phoneNumber}: InitFormValues) => {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(user, {
      phone_number: phoneNumber
    });
    await Auth.verifyUserAttribute(user, 'phone_number');
    setPhoneNumber(phoneNumber);
    setStep(Step.confirm);
    return updateCurrentUser(true);
  };

  const handleConfirm = async ({code}: ConfirmFormValues) => {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.verifyUserAttributeSubmit(user, 'phone_number', code);
    await Auth.setPreferredMFA(user, 'SMS');
    setMfaType('SMS_MFA');
    setStep(Step.success);
    return updateCurrentUser(true);
  };

  const handleClose = () => {
    setOpen(false);
    setStep(Step.init);
    setPhoneNumber(null);
  };

  const handleDisable = async () => {
    Modal.confirm({
      title: 'Are you sure?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Disable',
      okButtonProps: {danger: true},
      content: 'You are going to disable SMS authentication.',
      async onOk() {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.setPreferredMFA(user, 'NOMFA');
        handleClose();
        setMfaType('NOMFA');
      }
    });
  };

  if (!mfaType) return headless ? null : <Spin />;

  const button =
    mfaType === 'NOMFA' ? (
      <Button onClick={() => setOpen(true)}>Add SMS authentication</Button>
    ) : (
      <>
        <Paragraph strong type="success">
          <CheckOutlined /> Enabled
        </Paragraph>
        <Button onClick={handleDisable}>Disable SMS authentication</Button>
      </>
    );

  return (
    <>
      {headless ? null : button}
      <Modal
        destroyOnClose
        maskClosable={false}
        bodyStyle={{padding: 0}}
        title={step === Step.init ? 'Verify your mobile number' : 'Two-step authentication'}
        footer={false}
        width={450}
        open={isOpen}
        onCancel={handleClose}>
        {step === Step.init && (
          <Init onSubmit={handleInit} onCancel={handleClose} phoneNumber={userPhone || ''} />
        )}
        {step === Step.confirm && (
          <Confirm
            onSubmit={handleConfirm}
            onCancel={handleClose}
            phoneNumber={phoneNumber || ''}
          />
        )}
        {step === Step.success && (
          <>
            <ModalContent>
              <p>
                From now on, whenever you sign into your account, you'll need to enter both your
                password and an authentication code sent to your mobile phone.
              </p>
            </ModalContent>
            <ModalFooter>
              <Space>
                <Button onClick={handleClose}>Close</Button>
              </Space>
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );
};
